import format from 'date-format'

/* USAGE: 
 *
 * <script>
 *   const var1 = "red"
 *   const var2 = "blue"
 * </script>
 * 
 * <div style={styleVars({var1, var2})}></div>
 * 
 * <style>
 *  div {
 *    color: var(--var1);
 *    background-color: var(--var2)
 * }
 * </style>
 */

export function styleVars(vars) {
  return Object.entries(vars)
    .filter(([key, value]) => value !== 'url("null")' ) // quick fix for cascading faulty input
    .map( ([key, value]) => "--" + key + ": " + value )
    .join("; ")
}

// same as above but as a svelte action
export function styles(node, styles) {
  setCustomProperties(node, styles)

  return {
    update(styles) {
      setCustomProperties(node, styles)
    },
  }
}
function setCustomProperties(node, styles) {
  Object.entries(styles).forEach(([key, value]) => {
    node.style.setProperty(`--${key}`, value)
  })
}


// generate a string for "border-radius".
// there are rules how these whould be distributed among neighbouring items
//
// group: a string to indicate a group the item belongs to, for the distribution algorithm
// preset: override a the values, will return same values as string
const borderRadiusStore = {} // store the last values
export function generateBorderRadiusString(group = "default", preset = {large: undefined, small: undefined}) {

  const deltaMinLarge = 20 // minimum difference
  const valuesLarge = [
    [20, 40, 20, 40],
    [20, 50, 20, 50],
    [20, 60, 20, 60],
    [20, 70, 20, 70],
    [30, 50, 30, 50],
    [30, 60, 30, 60],
    [30, 70, 30, 70],
    [40, 20, 40, 20],
    [40, 60, 40, 60],
    [40, 70, 40, 70],
    [50, 20, 50, 20],
    [50, 30, 50, 30],
    [60, 20, 60, 20],
    [60, 30, 60, 30],
    [60, 40, 60, 40],
    [70, 20, 70, 20],
    [70, 30, 70, 30],
    [70, 40, 70, 40]
  ]

  const deltaMinSmall = 10 // minimum difference
  const valuesSmall = [
    [15, 30, 15, 30],
    [15, 35, 15, 35],
    [15, 40, 15, 40],
    [20, 35, 20, 35],
    [20, 40, 20, 40],
    [25, 40, 25, 40],
    [30, 15, 15, 30],
    [35, 15, 25, 15],
    [35, 20, 25, 20],
    [40, 15, 40, 15],
    [40, 20, 40, 20],
    [40, 25, 40, 25],
  ]

  function generateString(values, deltaMin, group, preset) {
    let result = preset
    if (!result) {
      let candidates = values
      if (borderRadiusStore[group]) { // previous value exists
        candidates = values.filter( v => Math.abs(borderRadiusStore[group][0] - v[0]) >= deltaMin )
      }
      result = pickRandom(candidates)
    }
    borderRadiusStore[group] = result
    return result.map(v => v + "px").join(" ")
  }

  return {
    largeString: generateString(valuesLarge, deltaMinLarge, group, preset.large),
    smallString: generateString(valuesSmall, deltaMinSmall, group, preset.small),
  }
}

function pickRandom(items) {
  return items[Math.floor(Math.random() * items.length)];
}


export function pannable(node) {
  let x;
  let y;

  function handleMousedown(event) {
    x = event.clientX;
    y = event.clientY;

    node.dispatchEvent(new CustomEvent('panstart', {
      detail: { x, y }
    }));

    window.addEventListener('mousemove', handleMousemove);
    window.addEventListener('mouseup', handleMouseup);
  }

  function handleMousemove(event) {
    const dx = event.clientX - x;
    const dy = event.clientY - y;
    x = event.clientX;
    y = event.clientY;

    node.dispatchEvent(new CustomEvent('panmove', {
      detail: { x, y, dx, dy }
    }));
  }

  function handleMouseup(event) {
    x = event.clientX;
    y = event.clientY;

    node.dispatchEvent(new CustomEvent('panend', {
      detail: { x, y }
    }));

    window.removeEventListener('mousemove', handleMousemove);
    window.removeEventListener('mouseup', handleMouseup);
  }

  node.addEventListener('mousedown', handleMousedown);

  return {
    destroy() {
      node.removeEventListener('mousedown', handleMousedown);
    }
  };
}

export function secondsToHMS(sec) {
  sec = Math.round(sec)
  const s = sec % 60
  const m = Math.floor( (sec - s) % 3600 / 60 )
  const h = Math.floor( (sec - (m*60)) % (24*3600) / 3600 )
  const s_string = `${s}`.padStart(2,0)
  const m_string = `${m}`.padStart(2, 0)
  const h_string = `${h}`
  return `${h_string}:${m_string}:${s_string}`
}


// var n = scaleValue(0, [-50,+50], [0,65535]);
export function scaleValue(value, from, to) {
  var scale = (to[1] - to[0]) / (from[1] - from[0]);
  var capped = Math.min(from[1], Math.max(from[0], value)) - from[0];
  return capped * scale + to[0];
}

export function limitValue(value, min, max) {
  if (value >= max) value = max
  if (value <= min) value = min
  return value
}


export function formatDate(date, style) {
  if (!date) return null
  switch(style) {
    case "day-month-year":
      return format.asString('dd.MM.yyyy', date)
      break;
    case "event-day-month":
      return format.asString('dd.MM.', date)
      break;
    case "event-day-month-year":
      return format.asString('dd.MM.yyyy', date)
      break;
    case "time24":
      return format.asString('hh:mm', date)
      break;
    case "livestream-featured":
      return format.asString('dd.MM., hh:mm', date)
    default: return null
  }
}

// sort by weight and then by startTime
export function sortEvents(a, b) {
  return (
      a["weight"]
    - b["weight"]
      ||
      a?.eventDates?.[0]?.startDateAndTime
    - b?.eventDates?.[0]?.startDateAndTime
  )
}

// fix http URLs
export function fixUrl(url) {
  return url?.replace("http://", "//")
}

export function urlsHaveSameHost(string1, string2) {
  if (string1 && string2 && typeof (URL) === "function") {
    try {
      const url1 = new URL(string1);
      const url2 = new URL(string2);
      return url1.hostname === url2.hostname
    } catch (error) {
      return false
    }
  }
  return false
}

// number to unicode supscript
export function numberToUnicodeSupscript(num) {
  let str = num + ""
  for (let i = 0; i<str.length; i++) {
    str = str.replace("1", "¹")
    str = str.replace("2", "²")
    str = str.replace("3", "³")
    str = str.replace("4", "⁴")
    str = str.replace("5", "⁵")
    str = str.replace("6", "⁶")
    str = str.replace("7", "⁷")
    str = str.replace("8", "⁸")
    str = str.replace("9", "⁹")
    str = str.replace("0", "⁰")
  }
  return str
}

// check if webp is supported by browser - takes a few milliseconds
let canUseWebPResultCache = null
export function canUseWebP() {
  if (canUseWebPResultCache !== null) return canUseWebPResultCache
  let result = false
  var elem = document.createElement('canvas');
  if (!!(elem.getContext && elem.getContext('2d'))) {
    // was able or not to get WebP representation
    result = elem.toDataURL('image/webp').indexOf('data:image/webp') == 0;
  }
  console.log("webp support is ", result)
  canUseWebPResultCache = result
  return result
}